import type { ReactNode } from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import type {
  DraggableAttributes,
  DraggableSyntheticListeners,
} from "@dnd-kit/core";

export type SortableRenderProps = {
  attributes: DraggableAttributes;
  listeners: DraggableSyntheticListeners;
  setNodeRef: (node: HTMLElement | null) => void;
  style: React.CSSProperties;
  isDragging: boolean;
};

/**
 * Exposes dnd-kit's sortable wiring through a render prop so the caller owns
 * the markup. `setNodeRef` goes on the element, `listeners` on the grip alone —
 * dragging from anywhere else would swallow clicks on the row's own controls.
 */
export function SortableItem({
  id,
  children,
}: {
  id: string;
  children: (props: SortableRenderProps) => ReactNode;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });
  return children({
    attributes,
    listeners,
    setNodeRef,
    isDragging,
    style: {
      transform: CSS.Transform.toString(transform),
      transition,
      // Lift the dragged row above its siblings; without it the row slides
      // under the next one because they share a stacking context.
      zIndex: isDragging ? 10 : undefined,
      opacity: isDragging ? 0.85 : undefined,
    },
  });
}
